<script setup>
/**
 * FormControlTemplatesTemplatesComponentId
 * A component selector that adapts to the template's content type.
 * It renders as plain text for existing items when not in beta mode.
 * Otherwise, it displays a dropdown of components, with its requirement and default
 * value dynamically changing based on the selected content type.
 */
import { ref, computed, watch, getCurrentInstance } from 'vue';
import FormControlTypeSelect from "../form-control-type/FormControlTypeSelect.vue";
import FormControlPlainText from "./FormControlPlainText.vue";

const { proxy } = getCurrentInstance();
const store = proxy.$store;

const props = defineProps({
    formId: String,
    field: Object,
    currentData: Object,
});

const title = ref('');

const components = computed(() => store.state.components.items);
const betaMode = computed(() => store.state.betaMode);
const contentType = computed(() => props.currentData.contentType);
const componentId = computed(() => props.currentData[props.field.name]);

const options = computed(() => {
    return components.value
        .filter(o => o.listing === 1 || o.tableName === 'm_registrants_registrants')
        .map(o => ({ value: o.id, text: o.title }))
        .sort((a, b) => (a.text > b.text) ? 1 : -1);
});

// The original component hardcoded this to true, so we will too.
const show = ref(true);

const fetchTitle = async (id) => {
    if (id) {
        try {
            const item = await store.dispatch('itemData/get', {
                tableName: 'modulecomponents',
                id: id,
            });
            title.value = item.title;
        } catch (e) {
            console.error('Failed to fetch component title', e);
            title.value = '';
        }
    } else if (id === 0) {
        title.value = 'All';
    } else {
        title.value = '';
    }
};

// Set options and default value on setup
props.field.options = options.value;
store.dispatch(`${props.formId}/setDefaultValue`, {
    name: 'componentId',
    value: 0,
});

watch(componentId, (newId) => {
    fetchTitle(newId);
}, { immediate: true });

watch(contentType, (newContentType) => {
    props.field.required = ['Filter', 'Listing', 'Tags', 'Teaser'].includes(newContentType);

    // When changing the content type, adjust the default value if nothing is selected
    if (props.currentData.componentId === '' || props.currentData.componentId === 0) {
        props.currentData.componentId = props.field.required ? '' : 0;
    }
});

// The original component assigned options in the created hook.
// If the source `components` can change reactively, this watch is needed.
watch(options, (newOptions) => {
    props.field.options = newOptions;
});

</script>

<template>
    <FormControlPlainText v-if="currentData.id && !betaMode" :value="title" :form-id="formId"/>
    <FormControlTypeSelect v-else-if="show" :current-data="currentData" :field="field" :form-id="formId"/>
</template>
